import React, { useRef, useEffect, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import { saveAs } from 'file-saver';
import {
  Box, Button, Typography, Slider, IconButton, Paper, CircularProgress,
  MenuItem, Tooltip, FormControlLabel, Switch
} from '@mui/material';
import {
  Stop, SkipPrevious, SkipNext,
  Replay, Videocam, Save, ErrorOutline,
  Fullscreen, FullscreenExit
} from '@mui/icons-material';

// Costanti di configurazione
const MAX_RECORDING_TIME = 60; // secondi massimi di registrazione
const DEFAULT_BUFFER_SIZE = 3; // secondi di buffer predefinito
const PLAYBACK_RATES = [0.25, 0.5, 1];

const VideoArbitraggioTool = () => {
  // Refs
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const reviewVideoRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const videoRef = useRef(null); // Riferimento unificato per il video
  const videoContainerRef = useRef(null); // Riferimento per il contenitore del video per il fullscreen

  // State
  const [selectedDevice, setSelectedDevice] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [bufferSize, setBufferSize] = useState(DEFAULT_BUFFER_SIZE);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [recordingTime, setRecordingTime] = useState(0);
  const [videoDevices, setVideoDevices] = useState([]);
  const [loopEnabled, setLoopEnabled] = useState(true);
  const [trimRange, setTrimRange] = useState({ start: 0, end: 100 });
  const [currentTime, setCurrentTime] = useState(0);
  const [showDeviceMenu, setShowDeviceMenu] = useState(false);
  const [showBufferMenu, setShowBufferMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Effetto per gestire l'uscita dal fullscreen con ESC e click esterno
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    const handleClickOutside = (event) => {
      const target = event.target;
      const isDeviceMenu = target.closest('.device-menu') || target.closest('.device-menu-button');
      const isBufferMenu = target.closest('.buffer-menu') || target.closest('.buffer-menu-button');

      if (!isDeviceMenu && showDeviceMenu) {
        setShowDeviceMenu(false);
      }
      if (!isBufferMenu && showBufferMenu) {
        setShowBufferMenu(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDeviceMenu, showBufferMenu]);

  // Effetto per il caricamento dei dispositivi
  useEffect(() => {
    const getDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setVideoDevices(videoDevices);
        if (videoDevices.length > 0) {
          setSelectedDevice(videoDevices[0].deviceId);
        }
        setIsLoading(false);
      } catch (err) {
        console.error('Errore nel caricamento dei dispositivi:', err);
        setError('Impossibile accedere ai dispositivi video');
        setIsLoading(false);
      }
    };

    getDevices();

    return () => {
      // Pulizia
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, []);

  // Gestione della selezione del dispositivo video
  const handleDeviceChange = (event) => {
    setSelectedDevice(event.target.value);
    setShowDeviceMenu(false);
  };

  // Gestione del fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoContainerRef.current?.requestFullscreen?.().catch(err => {
        console.error('Errore nell\'attivazione del fullscreen:', err);
      });
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Gestione cambio buffer size
  const handleBufferSizeChange = (newSize) => {
    setBufferSize(newSize);
    setShowBufferMenu(false);
  };

  // Interrompi la registrazione e mantieni solo gli ultimi 3 secondi
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      // Ferma la registrazione
      mediaRecorderRef.current.stop();
      clearInterval(recordingIntervalRef.current);
      setIsRecording(false);

      // Crea un video con solo gli ultimi 3 secondi
      const videoBlob = new Blob(chunksRef.current, { type: 'video/webm' });
      const videoUrl = URL.createObjectURL(videoBlob);

      const tempVideo = document.createElement('video');
      tempVideo.src = videoUrl;

      tempVideo.onloadedmetadata = () => {
        const duration = tempVideo.duration;
        const startTime = Math.max(0, duration - bufferSize);

        // Crea un canvas per il ritaglio
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        tempVideo.onseeked = () => {
          // Imposta le dimensioni del canvas
          canvas.width = tempVideo.videoWidth;
          canvas.height = tempVideo.videoHeight;

          // Disegna il frame corrente sul canvas
          ctx.drawImage(tempVideo, 0, 0, canvas.width, canvas.height);

          // Crea un nuovo blob con solo la parte ritagliata
          canvas.toBlob((blob) => {
            const trimmedUrl = URL.createObjectURL(blob);

            setRecordedVideo({
              url: trimmedUrl,
              blob: blob,
              timestamp: new Date().toISOString()
            });

            // Pulisci
            URL.revokeObjectURL(videoUrl);
          }, 'video/webm');
        };

        // Vai al punto di inizio del trim
        tempVideo.currentTime = startTime;
      };
    }
  }, [bufferSize]);

  // Avvia la registrazione
  const startRecording = useCallback(() => {
    if (!webcamRef.current || !webcamRef.current.video) {
      setError('Impossibile accedere alla webcam');
      return;
    }

    try {
      const stream = webcamRef.current.video.srcObject;
      if (!stream) {
        setError('Nessuno stream video disponibile');
        return;
      }

      // Configura il MediaRecorder
      const options = { mimeType: 'video/webm;codecs=vp9' };
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      setRecordingTime(0); // Resetta il contatore del tempo

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setRecordedVideo({
          url,
          blob,
          timestamp: new Date().toISOString()
        });
      };

      // Avvia la registrazione
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Timer di registrazione (aggiornamento ogni secondo)
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1; // Incrementa di 1 secondo
          if (newTime >= MAX_RECORDING_TIME) {
            stopRecording();
            return MAX_RECORDING_TIME;
          }
          return newTime;
        });
      }, 1000); // Aggiorna ogni secondo invece di ogni 100ms

    } catch (err) {
      console.error('Errore avvio registrazione:', err);
      setError(`Errore durante l'avvio della registrazione: ${err.message}`);
    }
  }, [stopRecording]);

  // Effetto per gestire il caricamento del video
  useEffect(() => {
    if (!recordedVideo?.url) return;

    const videoElement = videoRef.current;
    if (!videoElement) return;

    console.log('URL del video:', recordedVideo.url);
    let isInitialized = false;
    let checkInterval = null;

    const initializeVideo = () => {
      if (isInitialized) return;

      const checkDuration = () => {
        const duration = videoElement.duration;

        if (duration && isFinite(duration) && duration > 0) {
          const startTime = Math.max(0, duration - bufferSize);

          console.log('Durata rilevata:', duration, 'Imposto trim range:', { start: startTime, end: duration });

          // Aggiorna lo stato una sola volta
          if (!isInitialized) {
            setTrimRange({
              start: startTime,
              end: duration
            });

            // Imposta il tempo iniziale
            videoElement.currentTime = startTime;
            setCurrentTime(startTime);

            // Avvia la riproduzione
            videoElement.play().catch(e => {
              console.error('Errore nella riproduzione automatica:', e);
              videoElement.currentTime = startTime;
              setCurrentTime(startTime);
            });

            isInitialized = true;
            if (checkInterval) clearInterval(checkInterval);
            return true;
          }
        }
        return false;
      };

      // Prova subito
      if (!checkDuration()) {
        // Se non è ancora pronto, aspetta un po' e riprova
        checkInterval = setInterval(checkDuration, 100);
      }

      return () => {
        if (checkInterval) clearInterval(checkInterval);
      };
    };

    // Gestione eventi
    const handleLoadedMetadata = () => {
      console.log('loadedmetadata - durata:', videoElement.duration);
      initializeVideo();
    };

    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);

    // Se il video è già pronto, inizializza subito
    if (videoElement.readyState > 0) {
      initializeVideo();
    }

    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      if (checkInterval) clearInterval(checkInterval);
    };
  }, [recordedVideo?.url, bufferSize]);

  // Effetto per sincronizzare il tempo corrente con il video
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !recordedVideo?.url) return;

    const handleTimeUpdate = () => {
      if (videoElement && trimRange && isFinite(trimRange.start) && isFinite(trimRange.end)) {
        // Assicuriamoci che il tempo corrente sia nel range valido
        if (videoElement.currentTime < trimRange.start) {
          videoElement.currentTime = trimRange.start;
        } else if (videoElement.currentTime > trimRange.end) {
          videoElement.currentTime = trimRange.end;
        }
        setCurrentTime(videoElement.currentTime);
      }
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    return () => videoElement.removeEventListener('timeupdate', handleTimeUpdate);
  }, [recordedVideo?.url, trimRange]);

  // Effetto per gestire gli eventi del video
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !trimRange) return;

    console.log('Trim range aggiornato:', trimRange);

    const handleTimeUpdate = () => {
      if (!videoElement || !trimRange) return;

      // Aggiorna il tempo corrente
      setCurrentTime(videoElement.currentTime);

      // Gestisce il loop
      if (loopEnabled && videoElement.currentTime >= trimRange.end) {
        videoElement.currentTime = trimRange.start;
        videoElement.play().catch(e => console.error('Errore nel loop:', e));
      }
    };

    const handlePlay = () => {
      console.log('Play - Tempo corrente:', videoElement.currentTime);
      if (videoElement.currentTime < trimRange.start || videoElement.currentTime >= trimRange.end) {
        videoElement.currentTime = trimRange.start;
      }
    };

    const handlePause = () => {
      // isPlaying rimosso
    };

    const handleSeeked = () => {
      console.log('Seeked - Tempo corrente:', videoElement.currentTime);
      if (videoElement.currentTime < trimRange.start) {
        videoElement.currentTime = trimRange.start;
      } else if (videoElement.currentTime > trimRange.end) {
        videoElement.currentTime = trimRange.end;
      }
      setCurrentTime(videoElement.currentTime);
    };

    const handleEnded = () => {
      console.log('Video terminato');
      if (loopEnabled) {
        videoElement.currentTime = trimRange.start;
        videoElement.play().catch(e => console.error('Errore nel loop:', e));
      }
    };

    // Aggiungi l'evento timeupdate per il loop
    videoElement.addEventListener('timeupdate', handleTimeUpdate);

    // Aggiungi gli event listener
    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('ended', handleEnded);
    videoElement.addEventListener('seeked', handleSeeked);

    return () => {
      // Rimuovi gli event listener
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
      videoElement.removeEventListener('ended', handleEnded);
      videoElement.removeEventListener('seeked', handleSeeked);
    };
  }, [loopEnabled, trimRange]);

  // Riproduci il video registrato
  const togglePlayPause = useCallback(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !trimRange) return;

    if (videoElement.paused) {
      // Se siamo alla fine del range, torna all'inizio
      if (videoElement.currentTime >= trimRange.end - 0.1) {
        videoElement.currentTime = trimRange.start;
      }
      videoElement.play().catch(e => console.error('Errore nella riproduzione:', e));
    } else {
      videoElement.pause();
    }
  }, [trimRange]);

  // Cambia velocità di riproduzione
  const changePlaybackRate = useCallback((rate) => {
    const video = reviewVideoRef.current || videoRef.current;
    if (video) {
      console.log(`Impostazione velocità di riproduzione a: ${rate}x`);
      video.playbackRate = rate;
      setPlaybackRate(rate);
      // Forza l'aggiornamento della velocità anche durante la riproduzione
      if (!video.paused) {
        const currentTime = video.currentTime;
        video.pause();
        video.play().catch(e => console.error('Errore nel cambio velocità:', e));
        video.currentTime = currentTime;
      }
    } else {
      console.error('Nessun elemento video trovato');
    }
  }, []);

  // Avanza di un frame
  const stepFrame = useCallback((direction) => {
    const video = reviewVideoRef.current || videoRef.current;
    if (!video) return;
    const newTime = Math.max(0, video.currentTime + (1 / 30) * direction);
    video.currentTime = Math.min(newTime, video.duration);
  }, []);

  const handleError = useCallback((err) => {
    console.error('Errore webcam:', err);
    setError(`Errore di accesso alla webcam: ${err.message}`);
    setIsLoading(false);
  }, []);

  // Formatta il tempo in mm:ss
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Funzione per scaricare il video registrato
  const handleDownload = () => {
    if (recordedVideo?.url) {
      saveAs(recordedVideo.blob, `arbitraggio-${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.webm`);
    }
  };

  // Reset per una nuova registrazione
  const resetRecording = () => {
    if (recordedVideo) {
      URL.revokeObjectURL(recordedVideo.url);
      setRecordedVideo(null);
    }
    chunksRef.current = [];
    setRecordingTime(0);
  };

  // Render del componente
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" style={{ height: 'calc(100vh - 64px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
        <ErrorOutline color="error" sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          Errore
        </Typography>
        <Typography paragraph>{error}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
          startIcon={<Replay />}
        >
          Ricarica
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 },
      maxWidth: '100%',
      mx: 'auto',
      height: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative'
    }}>
      <Typography variant="h5" gutterBottom sx={{
        textAlign: 'center',
        fontWeight: 'bold',
        mb: 2,
        display: { xs: 'none', sm: 'block' }
      }}>
        Video Arbitraggio
      </Typography>

      {/* Contenitore principale con rapporto 16:9 */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        flex: '1 1 auto',
        maxHeight: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}>
        {!recordedVideo?.url ? (
          // Vista di registrazione
          <>
            <Paper
              ref={videoContainerRef}
              elevation={3}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: { xs: 0, sm: 2 },
                aspectRatio: '16/9',
                bgcolor: 'black',
                width: '100%',
                flex: '0 0 auto',
                mb: 1
              }}
            >
              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  videoConstraints={{
                    deviceId: selectedDevice,
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    frameRate: { ideal: 30 }
                  }}
                  onUserMediaError={handleError}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />

                {/* Controlli in alto a destra */}
                <Box sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 1,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start'
                }}>
                  {/* Menu a tendina per la selezione della telecamera */}
                  <Box sx={{ position: 'relative' }} className="device-menu">
                    <IconButton
                      className="device-menu-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeviceMenu(!showDeviceMenu);
                        setShowBufferMenu(false);
                      }}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
                        width: 32,
                        height: 32
                      }}
                    >
                      <Videocam fontSize="small" />
                    </IconButton>
                    {showDeviceMenu && (
                      <Paper sx={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        mt: 1,
                        minWidth: 200,
                        maxHeight: 200,
                        overflow: 'auto',
                        bgcolor: 'rgba(0,0,0,0.9)',
                        zIndex: 2
                      }}>
                        {videoDevices.map((device) => (
                          <MenuItem
                            key={device.deviceId}
                            selected={device.deviceId === selectedDevice}
                            onClick={() => handleDeviceChange({ target: { value: device.deviceId } })}
                            sx={{
                              color: 'white',
                              fontSize: '0.8rem',
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                              '&.Mui-selected': { bgcolor: 'primary.main' },
                              py: 0.5,
                              minHeight: 'auto'
                            }}
                          >
                            {device.label || `Telecamera ${device.deviceId.substring(0, 5)}`}
                          </MenuItem>
                        ))}
                      </Paper>
                    )}
                  </Box>

                  {/* Menu a tendina per la selezione del buffer */}
                  <Box sx={{ position: 'relative' }} className="buffer-menu">
                    <IconButton
                      className="buffer-menu-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowBufferMenu(!showBufferMenu);
                        setShowDeviceMenu(false);
                      }}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
                        width: 32,
                        height: 32
                      }}
                    >
                      <Box sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: '2px solid white',
                        position: 'relative',
                        '&::after': {
                          content: `"${bufferSize}"`,
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '0.6rem',
                          fontWeight: 'bold',
                          lineHeight: 1,
                          color: 'white'
                        }
                      }} />
                    </IconButton>
                    {showBufferMenu && (
                      <Paper sx={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        mt: 1,
                        minWidth: 120,
                        bgcolor: 'rgba(0,0,0,0.9)',
                        zIndex: 2
                      }}>
                        {[1, 2, 3, 5, 10].map((size) => (
                          <MenuItem
                            key={size}
                            selected={size === bufferSize}
                            onClick={() => handleBufferSizeChange(size)}
                            sx={{
                              color: 'white',
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                              '&.Mui-selected': { bgcolor: 'primary.main' }
                            }}
                          >
                            {size} secondi
                          </MenuItem>
                        ))}
                      </Paper>
                    )}
                  </Box>

                  {/* Pulsante Fullscreen */}
                  <Tooltip title={isFullscreen ? 'Esci da schermo intero' : 'Ingrandisci'}>
                    <IconButton
                      onClick={toggleFullscreen}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
                        width: 32,
                        height: 32
                      }}
                    >
                      {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                    </IconButton>
                  </Tooltip>


                </Box>
                {/* Fine controlli in alto a destra */}
              </Box> {/* Chiusura del Box che contiene Webcam e controlli */}

              {isRecording && (
                <Box sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  bgcolor: 'error.main',
                  color: 'white',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  zIndex: 1
                }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    bgcolor: 'white',
                    borderRadius: '50%',
                    mr: 1,
                    animation: 'pulse 1.5s infinite'
                  }} />
                  <Typography variant="caption" fontWeight="bold">
                    REGISTRAZIONE
                  </Typography>
                </Box>
              )}

              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography variant="h6" color="white" fontWeight="bold">
                  {formatTime(recordingTime)}
                </Typography>
                <IconButton
                  onClick={isRecording ? stopRecording : startRecording}
                  sx={{
                    width: 50,
                    height: 50,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'error.dark',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s',
                    boxShadow: 3,
                    mb: 1
                  }}
                >
                  {isRecording ? (
                    <Stop sx={{ fontSize: 26 }} />
                  ) : (
                    <Box sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: 'white'
                    }} />
                  )}
                </IconButton>


              </Box>
            </Paper>
          </>
        ) : (
          // Vista di analisi
          <>
            <Paper
              ref={videoContainerRef}
              elevation={3}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: { xs: 0, sm: 2 },
                aspectRatio: '16/9',
                bgcolor: 'black',
                width: '100%',
                flex: '0 0 auto',
                mb: 1,
                '&:hover .video-controls-overlay': {
                  opacity: 1
                },
                '&:fullscreen': {
                  width: '100vw',
                  height: '100vh',
                  borderRadius: 0,
                  '& video': {
                    objectFit: 'contain',
                    width: '100%',
                    height: '100%'
                  }
                }
              }}
              onClick={(e) => {
                // Se il click non è su un pulsante, gestisci play/pause
                if (!e.target.closest('button')) {
                  togglePlayPause();
                }
              }}
            >
              <video
                ref={videoRef}
                src={recordedVideo?.url}
                controls={false}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  cursor: 'pointer'
                }}
                playsInline
              />

              {/* Overlay per il click su play/pause */}
              <Box
                className="video-controls-overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlayPause();
                }}
              />

              {/* Navigazione frame by frame */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: '30%',
                  cursor: 'w-resize',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  pl: 2,
                  '&:hover': {
                    background: 'linear-gradient(90deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 100%)',
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  stepFrame(-1);
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s ease-in-out',
                    '&:hover': {
                      opacity: 1,
                      bgcolor: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                >
                  <SkipPrevious />
                </Box>
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: '30%',
                  cursor: 'e-resize',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: 2,
                  '&:hover': {
                    background: 'linear-gradient(270deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 100%)',
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  stepFrame(1);
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s ease-in-out',
                    '&:hover': {
                      opacity: 1,
                      bgcolor: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                >
                  <SkipNext />
                </Box>
              </Box>

              {/* Barra superiore dei controlli */}
              <Box sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                right: 10,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'rgba(0,0,0,0.5)',
                p: 1,
                borderRadius: 1
              }}>
                {/* Pulsanti di azione a sinistra */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Nuova registrazione">
                    <IconButton
                      onClick={resetRecording}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                        width: 32,
                        height: 32
                      }}
                    >
                      <Replay fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Salva video">
                    <IconButton
                      onClick={handleDownload}
                      size="small"
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        width: 32,
                        height: 32
                      }}
                    >
                      <Save fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Selettore velocità al centro */}
                <Paper sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'rgba(0,0,0,0.7)',
                  borderRadius: 16,
                  overflow: 'hidden',
                  mx: 'auto'
                }}>
                  {PLAYBACK_RATES.map((rate) => (
                    <Button
                      key={rate}
                      size="small"
                      onClick={() => changePlaybackRate(rate)}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        color: 'white',
                        bgcolor: playbackRate === rate ? 'primary.main' : 'transparent',
                        borderRadius: 0,
                        '&:hover': {
                          bgcolor: playbackRate === rate ? 'primary.dark' : 'rgba(255,255,255,0.1)'
                        },
                        fontSize: '0.75rem',
                        fontWeight: playbackRate === rate ? 'bold' : 'normal'
                      }}
                    >
                      {rate}x
                    </Button>
                  ))}
                </Paper>


                {/* Controlli a destra */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* Menu a tendina per la selezione della telecamera */}
                  <Box sx={{ position: 'relative' }} className="device-menu">
                    <Tooltip title="Seleziona telecamera">
                      <IconButton
                        className="device-menu-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeviceMenu(!showDeviceMenu);
                          setShowBufferMenu(false);
                        }}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.1)',
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                          width: 32,
                          height: 32
                        }}
                      >
                        <Videocam fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {showDeviceMenu && (
                      <Paper sx={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        mt: 1,
                        minWidth: 200,
                        bgcolor: 'background.paper',
                        boxShadow: 3,
                        zIndex: 10,
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}>
                        {videoDevices.map((device) => (
                          <Box
                            key={device.deviceId}
                            onClick={() => {
                              setSelectedDevice(device.deviceId);
                              setShowDeviceMenu(false);
                            }}
                            sx={{
                              px: 2,
                              py: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: 'action.hover'
                              },
                              bgcolor: selectedDevice === device.deviceId ? 'action.selected' : 'transparent'
                            }}
                          >
                            <Typography variant="body2">
                              {device.label || `Telecamera ${videoDevices.indexOf(device) + 1}`}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    )}
                  </Box>

                  {/* Menu a tendina per la selezione del buffer */}
                  <Box sx={{ position: 'relative' }} className="buffer-menu">
                    <Tooltip title={`Buffer: ${bufferSize}s`}>
                      <IconButton
                        className="buffer-menu-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowBufferMenu(!showBufferMenu);
                          setShowDeviceMenu(false);
                        }}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.1)',
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                          width: 32,
                          height: 32
                        }}
                      >
                        <Box sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          border: '2px solid white',
                          position: 'relative',
                          '&::after': {
                            content: `"${bufferSize}"`,
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            fontSize: '0.6rem',
                            fontWeight: 'bold',
                            lineHeight: 1,
                            color: 'white'
                          }
                        }} />
                      </IconButton>
                    </Tooltip>
                    {showBufferMenu && (
                      <Paper sx={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        mt: 1,
                        minWidth: 80,
                        bgcolor: 'background.paper',
                        boxShadow: 3,
                        zIndex: 10,
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}>
                        {[1, 2, 3, 5, 10].map((size) => (
                          <Box
                            key={size}
                            onClick={() => {
                              setBufferSize(size);
                              setShowBufferMenu(false);
                            }}
                            sx={{
                              px: 2,
                              py: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: 'action.hover'
                              },
                              bgcolor: bufferSize === size ? 'action.selected' : 'transparent'
                            }}
                          >
                            <Typography variant="body2">
                              {size}s
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    )}
                  </Box>

                  {/* Pulsante Fullscreen */}
                  <Tooltip title={isFullscreen ? 'Esci da schermo intero' : 'Ingrandisci'}>
                    <IconButton
                      onClick={toggleFullscreen}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                        width: 32,
                        height: 32
                      }}
                    >
                      {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {/* Pulsanti di azione a sinistra */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Nuova registrazione">
                  <IconButton
                    onClick={resetRecording}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                      width: 32,
                      height: 32
                    }}
                  >
                    <Replay fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Salva video">
                  <IconButton
                    onClick={handleDownload}
                    size="small"
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' },
                      width: 32,
                      height: 32
                    }}
                  >
                    <Save fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Controlli a destra */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Menu a tendina per la selezione della telecamera */}
                <Box sx={{ position: 'relative' }} className="device-menu">
                  <Tooltip title="Seleziona telecamera">
                    <IconButton
                      className="device-menu-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeviceMenu(!showDeviceMenu);
                        setShowBufferMenu(false);
                      }}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                        width: 32,
                        height: 32
                      }}
                    >
                      <Videocam fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {showDeviceMenu && (
                    <Paper sx={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      mt: 1,
                      minWidth: 200,
                      bgcolor: 'background.paper',
                      boxShadow: 3,
                      zIndex: 10,
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}>
                      {videoDevices.map((device) => (
                        <Box
                          key={device.deviceId}
                          onClick={() => {
                            setSelectedDevice(device.deviceId);
                            setShowDeviceMenu(false);
                          }}
                          sx={{
                            px: 2,
                            py: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: 'action.hover'
                            },
                            bgcolor: selectedDevice === device.deviceId ? 'action.selected' : 'transparent'
                          }}
                        >
                          <Typography variant="body2">
                            {device.label || `Telecamera ${videoDevices.indexOf(device) + 1}`}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Box>


                {/* Menu a tendina per la selezione del buffer */}
                <Box sx={{ position: 'relative' }} className="buffer-menu">
                  <Tooltip title={`Buffer: ${bufferSize}s`}>
                    <IconButton
                      className="buffer-menu-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowBufferMenu(!showBufferMenu);
                        setShowDeviceMenu(false);
                      }}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                        width: 32,
                        height: 32
                      }}
                    >
                      <Box sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: '2px solid white',
                        position: 'relative',
                        '&::after': {
                          content: `"${bufferSize}"`,
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '0.6rem',
                          fontWeight: 'bold',
                          lineHeight: 1,
                          color: 'white'
                        }
                      }} />
                    </IconButton>
                  </Tooltip>
                  {showBufferMenu && (
                    <Paper sx={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      mt: 1,
                      minWidth: 80,
                      bgcolor: 'background.paper',
                      boxShadow: 3,
                      zIndex: 10,
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}>
                      {[1, 2, 3, 5, 10].map((size) => (
                        <Box
                          key={size}
                          onClick={() => {
                            setBufferSize(size);
                            setShowBufferMenu(false);
                          }}
                          sx={{
                            px: 2,
                            py: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: 'action.hover'
                            },
                            bgcolor: bufferSize === size ? 'action.selected' : 'transparent'
                          }}
                        >
                          <Typography variant="body2">
                            {size}s
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Box>


                {/* Pulsante Fullscreen */}
                <Tooltip title={isFullscreen ? 'Esci da schermo intero' : 'Ingrandisci'}>
                  <IconButton
                    onClick={toggleFullscreen}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                      width: 32,
                      height: 32
                    }}
                  >
                    {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Barra di riproduzione */}
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                p: 1,
                bgcolor: 'rgba(0,0,0,0.7)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="caption" color="white" sx={{ fontSize: '0.7rem', minWidth: 80, textAlign: 'left' }}>
                    {Math.floor(currentTime - trimRange.start)}s / {Math.ceil(trimRange.end - trimRange.start)}s
                  </Typography>
                  <Box sx={{ flexGrow: 1, px: 1 }}>
                    <Slider
                      value={isFinite(currentTime - trimRange.start) ? currentTime - trimRange.start : 0}
                      onChange={(_, newValue) => {
                        if (videoRef.current) {
                          const newTime = Math.min(trimRange.start + Number(newValue), trimRange.end);
                          if (isFinite(newTime)) {
                            videoRef.current.currentTime = newTime;
                            setCurrentTime(newTime);
                          }
                        }
                      }}
                      min={0}
                      max={isFinite(trimRange.end - trimRange.start) ? trimRange.end - trimRange.start : 0.1}
                      step={0.1}
                      sx={{ width: '100%' }}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${Math.min(Number(value), trimRange.end - trimRange.start).toFixed(1)}s`}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={loopEnabled}
                        onChange={(e) => setLoopEnabled(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="caption" color="white" sx={{ fontSize: '0.7rem' }}>
                        Loop
                      </Typography>
                    }
                    labelPlacement="start"
                  />
                </Box>
              </Box>

              {/* Rimuovi l'overlay di analisi */}
            </Paper>

            {/* Spazio per il footer */}
            <Box sx={{ height: 16 }} />
          </>
        )}
      </Box>

      <style>{
        `@keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }`
      }</style>
    </Box>
  );
}

export default VideoArbitraggioTool;
